import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  limit,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { EncryptionService } from './encryptionService';

const DIARIES_COLLECTION = 'diaries';

export interface DiaryEntry {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD format
  content: string;
  labels: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isLocked?: boolean; // Flag for decryption failure
}

export interface DiaryInput {
  date: string;
  content?: string;
  labels?: string[];
}

export interface DiaryUpdate {
  content?: string;
  labels?: string[];
}

// Create a new diary entry
export const createDiaryEntry = async (
  userId: string,
  diaryData: DiaryInput,
  passkey: string
): Promise<string> => {
  try {
    // Encrypt content before storing
    const encryptedContent = EncryptionService.encrypt(
      diaryData.content || '',
      passkey
    );

    const diaryRef = await addDoc(collection(db, DIARIES_COLLECTION), {
      userId,
      date: diaryData.date,
      content: encryptedContent,
      labels: diaryData.labels || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return diaryRef.id;
  } catch (error) {
    console.error('Error creating diary entry:', error);
    throw error;
  }
};

// Get diary entry for a specific date
export const getDiaryByDate = async (
  userId: string,
  date: string,
  passkey: string
): Promise<DiaryEntry | null> => {
  try {
    const q = query(
      collection(db, DIARIES_COLLECTION),
      where('userId', '==', userId),
      where('date', '==', date)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const docSnap = querySnapshot.docs[0];
    const data = docSnap.data();

    try {
      const decryptedContent = EncryptionService.decrypt(data.content || '', passkey);
      return {
        id: docSnap.id,
        ...data,
        content: decryptedContent,
        isLocked: false,
      } as DiaryEntry;
    } catch (error) {
      console.error('Error decrypting diary entry:', error);
      return {
        id: docSnap.id,
        ...data,
        content: '',
        isLocked: true,
      } as DiaryEntry;
    }
  } catch (error) {
    console.error('Error getting diary entry:', error);
    throw error;
  }
};

// Get diary dates (paginated) - returns just dates for the list view
export const getDiaryDates = async (
  userId: string,
  limitCount: number = 30,
  lastDoc?: DocumentSnapshot,
  labelFilter: string | null = null
): Promise<{ dates: { id: string; date: string; hasContent: boolean; labels: string[] }[]; lastDoc: DocumentSnapshot | null }> => {
  try {
    let q;

    if (labelFilter && labelFilter !== 'all') {
      if (lastDoc) {
        q = query(
          collection(db, DIARIES_COLLECTION),
          where('userId', '==', userId),
          where('labels', 'array-contains', labelFilter),
          orderBy('date', 'desc'),
          startAfter(lastDoc),
          limit(limitCount)
        );
      } else {
        q = query(
          collection(db, DIARIES_COLLECTION),
          where('userId', '==', userId),
          where('labels', 'array-contains', labelFilter),
          orderBy('date', 'desc'),
          limit(limitCount)
        );
      }
    } else {
      if (lastDoc) {
        q = query(
          collection(db, DIARIES_COLLECTION),
          where('userId', '==', userId),
          orderBy('date', 'desc'),
          startAfter(lastDoc),
          limit(limitCount)
        );
      } else {
        q = query(
          collection(db, DIARIES_COLLECTION),
          where('userId', '==', userId),
          orderBy('date', 'desc'),
          limit(limitCount)
        );
      }
    }

    const querySnapshot = await getDocs(q);
    const dates = querySnapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        date: data.date,
        hasContent: !!data.content && data.content.length > 0,
        labels: data.labels || [],
      };
    });

    const newLastDoc = querySnapshot.docs.length > 0 
      ? querySnapshot.docs[querySnapshot.docs.length - 1] 
      : null;

    return { dates, lastDoc: newLastDoc };
  } catch (error) {
    console.error('Error getting diary dates:', error);
    throw error;
  }
};

// Get all diary entries for a user (for re-encryption)
export const getAllDiaryEntries = async (
  userId: string,
  passkey: string
): Promise<DiaryEntry[]> => {
  try {
    const q = query(
      collection(db, DIARIES_COLLECTION),
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const entries: DiaryEntry[] = [];

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      try {
        const decryptedContent = EncryptionService.decrypt(data.content || '', passkey);
        entries.push({
          id: docSnap.id,
          ...data,
          content: decryptedContent,
          isLocked: false,
        } as DiaryEntry);
      } catch (error) {
        entries.push({
          id: docSnap.id,
          ...data,
          content: '',
          isLocked: true,
        } as DiaryEntry);
      }
    });

    return entries;
  } catch (error) {
    console.error('Error getting all diary entries:', error);
    throw error;
  }
};

// Update a diary entry
export const updateDiaryEntry = async (
  diaryId: string,
  updates: DiaryUpdate,
  passkey: string
): Promise<void> => {
  try {
    const diaryRef = doc(db, DIARIES_COLLECTION, diaryId);
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };

    if (updates.content !== undefined) {
      updateData.content = EncryptionService.encrypt(updates.content, passkey);
    }
    if (updates.labels !== undefined) {
      updateData.labels = updates.labels;
    }

    await updateDoc(diaryRef, updateData);
  } catch (error) {
    console.error('Error updating diary entry:', error);
    throw error;
  }
};

// Delete a diary entry
export const deleteDiaryEntry = async (diaryId: string): Promise<void> => {
  try {
    const diaryRef = doc(db, DIARIES_COLLECTION, diaryId);
    await deleteDoc(diaryRef);
  } catch (error) {
    console.error('Error deleting diary entry:', error);
    throw error;
  }
};

// Get all diary labels for a user
export const getDiaryLabels = async (userId: string): Promise<string[]> => {
  try {
    const q = query(
      collection(db, DIARIES_COLLECTION),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const labelsSet = new Set<string>();

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.labels && Array.isArray(data.labels)) {
        data.labels.forEach((label: string) => labelsSet.add(label));
      }
    });

    return Array.from(labelsSet).sort();
  } catch (error) {
    console.error('Error getting diary labels:', error);
    throw error;
  }
};

// Re-encrypt all diary entries with new passkey
export const reEncryptDiaryEntries = async (
  userId: string,
  oldPasskey: string,
  newPasskey: string,
  onProgress?: (current: number, total: number) => void
): Promise<{ success: number; failed: number }> => {
  try {
    const entries = await getAllDiaryEntries(userId, oldPasskey);
    let success = 0;
    let failed = 0;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      
      if (entry.isLocked) {
        failed++;
        onProgress?.(i + 1, entries.length);
        continue;
      }

      try {
        await updateDiaryEntry(entry.id, { content: entry.content }, newPasskey);
        success++;
      } catch (error) {
        console.error(`Failed to re-encrypt diary ${entry.id}:`, error);
        failed++;
      }

      onProgress?.(i + 1, entries.length);
    }

    return { success, failed };
  } catch (error) {
    console.error('Error re-encrypting diary entries:', error);
    throw error;
  }
};

